import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const ROLE_OPTIONS = [
  { value: 'private', label: 'Private', desc: 'I collect and buy art privately' },
  { value: 'dealer', label: 'Dealer', desc: 'I trade and deal in artworks' },
  { value: 'advisor', label: 'Art Advisor', desc: 'I advise clients on art acquisitions' },
];

export default function ProfileComplete() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || '',
    profile_type: user?.profile_type || '',
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    phone: user?.phone || '',
    portfolio_url: user?.portfolio_url || '',
  });
  const [loading, setLoading] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState('GB'); // default to GB

  React.useEffect(() => {
    fetch('https://ipwho.is/')
      .then(res => res.json())
      .then(data => {
        if (data && data.country_code) {
          setDefaultCountry(data.country_code.toLowerCase());
        }
        if (data && data.city && data.country) {
          setForm(f => f.location ? f : { ...f, location: `${data.city}, ${data.country}` });
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.profile_type) { toast.error('Please select your role'); return; }
    
    // The phone value includes the country code (e.g. +91)
    // A valid international phone number should be at least 8 characters long
    if (!form.phone || form.phone.length < 8) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      await api.put('/profile', form);
      const updated = await refreshUser();
      toast.success('Profile saved!');
      if (updated.membership_status === 'none') navigate('/membership/apply');
      else navigate('/membership/status');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-subtle)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container-app py-10">
        <div className="max-w-xl mx-auto">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-widest mb-2" style={{ color: 'var(--gold)' }}>Step 1 of 2</p>
            <h1 className="text-3xl lg:text-4xl font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Complete your profile</h1>
            <p className="text-base" style={{ color: 'var(--text-muted)' }}>Tell us about yourself so our team can review your membership application.</p>
          </div>

          <div className="p-8 rounded-2xl border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role */}
              <div>
                <Label className="text-sm font-medium mb-3 block" style={{ color: 'var(--text-muted)' }}>I am a <span className="text-red-500">*</span></Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="profile-role-radio">
                  {ROLE_OPTIONS.map(r => (
                    <button key={r.value} type="button" onClick={() => setForm(f => ({...f, profile_type: r.value}))}
                      className="p-4 rounded-xl border text-left transition-colors"
                      style={{
                        borderColor: form.profile_type === r.value ? 'var(--gold)' : 'var(--border-clr)',
                        backgroundColor: form.profile_type === r.value ? 'var(--gold-pale)' : 'var(--surface)',
                        cursor: 'pointer',
                      }}>
                      <p className="text-base font-medium" style={{ color: form.profile_type === r.value ? 'var(--text)' : 'var(--text-muted)' }}>{r.label}</p>
                      <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Full Name <span className="text-red-500">*</span></Label>
                <Input required className="h-11 text-base" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Bio <span className="text-red-500">*</span></Label>
                <Textarea required className="text-base p-3" placeholder="Tell us about your background in art..." value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} rows={3} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Location <span className="text-red-500">*</span></Label>
                  <Input required className="h-11 text-base" placeholder="City, Country" value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Phone <span className="text-red-500">*</span></Label>
                  <PhoneInput
                    country={defaultCountry}
                    value={form.phone}
                    onChange={value => setForm(f => ({...f, phone: value ? '+' + value : ''}))}
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    inputProps={{ required: true }}
                    inputClass="!w-full !h-11 !text-base !rounded-md !border !border-gray-200 focus:!border-black"
                    buttonClass="!border-gray-200 !rounded-l-md !bg-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Website</Label>
                <Input className="h-11 text-base" placeholder="https://yourgallery.com" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <Button type="submit" className="w-full h-12 text-base font-medium mt-4" disabled={loading} data-testid="profile-complete-submit-button" style={{ backgroundColor: 'var(--text)', color: 'white', borderRadius: '10px' }}>
                {loading ? 'Saving...' : 'Save & Continue'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
